import { module as Module } from 'modujs';
import { FONT } from '../config';
import { whenReady } from '../utils/fonts';
import { $screenDebounce } from '../stores/screen';
import { clamp, mapRange, roundNumber } from '../utils/maths';

export default class Timeline extends Module {
    static STAGGER_OFFSET = 0.1;
    static MQ_FROM_MEDIUM = `(min-width: 1000px)`;

    constructor(m) {
        super(m);

        // Binding
        this.onResize = this.onResize.bind(this);

        // UI
        this.$items = Array.from(this.$('item'));
        this.$years = Array.from(this.$('year'));

        // Data
        this.progress = 0;
        this.wHeight = window.innerHeight;
        this.metrics = [];
    }

    // =============================================================================
    // Lifecycle
    // =============================================================================
    init() {
        whenReady(FONT.EAGER).then((fonts) => this.onFontsLoaded(fonts));
        this.bindEvents();
    }

    destroy() {
        super.destroy();
        this.unbindEvents();
    }

    // =============================================================================
    // Events
    // =============================================================================
    bindEvents() {
        this.unbindScreenListener = $screenDebounce.subscribe(this.onResize);
    }

    unbindEvents() {
        this.unbindScreenListener?.();
    }

    // =============================================================================
    // Callbacks
    // =============================================================================
    onFontsLoaded(fonts) {
        if (window.matchMedia(Timeline.MQ_FROM_MEDIUM).matches) {
            this.setMetrics();
        }
    }

    onResize({ width, height }) {
        this.wHeight = height;

        if (window.matchMedia(Timeline.MQ_FROM_MEDIUM).matches) {
            this.setMetrics();
        }
    }

    onScrollProgress(progress) {
        if (window.matchMedia(Timeline.MQ_FROM_MEDIUM).matches) {
            this.progress = progress;
            this.setStepProgress();
        }
    }

    // =============================================================================
    // Methods
    // =============================================================================
    setMetrics() {
        let acc = 0;
        const totalHeight = this.el.offsetHeight;
        this.metrics = this.$items.map(($item) => {
            const itemHeight = $item.offsetHeight;

            const itemMetrics = {
                progressStart: acc / totalHeight,
                progressEnd: (acc + this.wHeight) / totalHeight
            };

            acc += itemHeight;

            return itemMetrics;
        });
    }

    setStepProgress() {
        this.$years.forEach(($year, i) => {
            const { progressStart, progressEnd } = this.metrics[i];
            const { progressStart: progressStartNext, progressEnd: progressEndNext } =
                this.metrics[i + 1] ?? {};

            const halfStartProgress =
                i === 0
                    ? 0.5
                    : clamp(0, 0.5, mapRange(progressStart, progressEnd, 0, 0.5, this.progress));
            const halfEnd = this.metrics[i + 1]
                ? clamp(0, 0.5, mapRange(progressStartNext, progressEndNext, 0, 0.5, this.progress))
                : 0;

            const progressesStarts = [
                clamp(
                    0,
                    0.5,
                    mapRange(0, 0.5 - Timeline.STAGGER_OFFSET * 3, 0, 0.5, halfStartProgress)
                ),
                clamp(
                    0,
                    0.5,
                    mapRange(0, 0.5 - Timeline.STAGGER_OFFSET * 2, 0, 0.5, halfStartProgress)
                ),
                clamp(
                    0,
                    0.5,
                    mapRange(0, 0.5 - Timeline.STAGGER_OFFSET, 0, 0.5, halfStartProgress)
                ),
                halfStartProgress
            ];
            const progressesEnds = [
                clamp(0, 0.5, mapRange(0, 0.5 - Timeline.STAGGER_OFFSET * 3, 0, 0.5, halfEnd)),
                clamp(0, 0.5, mapRange(0, 0.5 - Timeline.STAGGER_OFFSET * 2, 0, 0.5, halfEnd)),
                clamp(0, 0.5, mapRange(0, 0.5 - Timeline.STAGGER_OFFSET, 0, 0.5, halfEnd)),
                halfEnd
            ];

            for (let i = 0; i < 4; i++) {
                const progressStart = progressesStarts[i];
                const progressEnd = progressesEnds[i];
                $year.style.setProperty(
                    `--progress-${i}`,
                    roundNumber(progressStart + progressEnd, 4)
                );
            }
        });
    }
}
